// third-party
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// project imports
import { openSnackbar } from 'store/slices/snackbar';

// types
import { DefaultRootStateProps } from 'types';
import { FacebookType, GoogleType, listServices, MicrosoftType, OAUTHType, SAMLType, Webhook } from 'types/chat-gpt';
import {
    createEditWebhookService,
    createLoginMethodsService,
    createMerchantService,
    editLoginMethodsService,
    editMerchantService,
    getBalanceService,
    getBillingService,
    getLoginMethodsService,
    getTermsService,
    getWebhooksService,
    handleIsActiveService,
    isActiveService,
    patchConditionsService,
    sendInvoiceService
} from 'services/ai';

interface CreateMerchant {
    name: string;
    tenant: string;
    status?: boolean;
    url: string;
    imageUrl: string;
    appKey: string;
    appToken: string;
    environment: string;
}

interface HandleIsActive {
    isActive?: boolean;
    serviceName: string;
    apiKey?: string;
}

interface EditMerchant {
    merchant: string;
    merchantId: number;
    tenantId: string;
    status?: boolean;
}

const initialState: DefaultRootStateProps['aiConfig'] = {
    loading: true,
    isGPT: null,
    isGemini: null,
    webhooks: null,
    billing: null,
    logInMethods: null,
    loadingEmailSent: false,
    balance: null,
    terms: null
};

const slice = createSlice({
    name: 'aiConfigs',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(isActive.pending, (state) => {
                state.loading = true;
            })
            .addCase(isActive.fulfilled, (state, action) => {
                state.loading = false;
                const listServices = action.payload || [];
                listServices.length &&
                    listServices.forEach((service: listServices) => {
                        if (service.ServiceName === 'gemini') {
                            state.isGemini = service;
                        }
                        if (service.ServiceName === 'gpt') {
                            state.isGPT = service;
                        }
                    });
            })
            .addCase(isActive.rejected, (state) => {
                state.loading = false;
            })
            .addCase(handleIsActive.pending, (state) => {
                state.loading = true;
            })
            .addCase(handleIsActive.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(handleIsActive.rejected, (state) => {
                state.loading = false;
            })
            .addCase(editMerchant.pending, (state) => {
                state.loading = true;
            })
            .addCase(editMerchant.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(editMerchant.rejected, (state) => {
                state.loading = false;
            })
            .addCase(createMerchant.pending, (state) => {
                state.loading = true;
            })
            .addCase(createMerchant.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(createMerchant.rejected, (state) => {
                state.loading = false;
            })
            .addCase(createEditWebhook.pending, (state) => {
                state.loading = true;
            })
            .addCase(createEditWebhook.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(createEditWebhook.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getWebhooks.pending, (state) => {
                state.loading = true;
            })
            .addCase(getWebhooks.fulfilled, (state, action) => {
                state.loading = false;
                state.webhooks = action.payload;
            })
            .addCase(getWebhooks.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getBilling.pending, (state) => {
                state.loading = true;
            })
            .addCase(getBilling.fulfilled, (state, action) => {
                state.loading = false;
                state.billing = action.payload;
            })
            .addCase(getBilling.rejected, (state) => {
                state.loading = false;
            })
            .addCase(sendInvoice.pending, (state) => {
                state.loadingEmailSent = true;
            })
            .addCase(sendInvoice.fulfilled, (state) => {
                state.loadingEmailSent = false;
            })
            .addCase(sendInvoice.rejected, (state) => {
                state.loadingEmailSent = false;
            })
            .addCase(getLoginMethods.pending, (state) => {
                state.loadingEmailSent = true;
            })
            .addCase(getLoginMethods.fulfilled, (state, action) => {
                state.loading = false;
                state.logInMethods = action.payload;
            })
            .addCase(getLoginMethods.rejected, (state) => {
                state.loadingEmailSent = false;
            })
            .addCase(editLoginMethods.pending, (state) => {
                state.loadingEmailSent = true;
            })
            .addCase(editLoginMethods.fulfilled, (state) => {
                state.loadingEmailSent = false;
            })
            .addCase(editLoginMethods.rejected, (state) => {
                state.loadingEmailSent = false;
            })
            .addCase(createLoginMethods.pending, (state) => {
                state.loadingEmailSent = true;
            })
            .addCase(createLoginMethods.fulfilled, (state) => {
                state.loadingEmailSent = false;
            })
            .addCase(createLoginMethods.rejected, (state) => {
                state.loadingEmailSent = false;
            })
            .addCase(getBalance.pending, (state) => {
                state.loading = true;
            })
            .addCase(getBalance.fulfilled, (state, action) => {
                state.balance = action.payload;
                state.loading = false;
            })
            .addCase(getBalance.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getTerms.pending, (state) => {
                state.loading = true;
            })
            .addCase(getTerms.fulfilled, (state, action) => {
                state.terms = action.payload;
                state.loading = false;
            })
            .addCase(getTerms.rejected, (state) => {
                state.loading = false;
            });
    }
});
export default slice.reducer;

export const isActive = createAsyncThunk(`${slice.name}/isActive`, async (_arg, { dispatch }) => {
    try {
        const response = await isActiveService();
        return response.data.response;
    } catch (error) {
        dispatch(
            openSnackbar({
                message: 'An error occurred while fetching the AI configurations.',
                variant: 'error'
            })
        );
    }
});

export const handleIsActive = createAsyncThunk(`${slice.name}/handleIsActive`, async (params: HandleIsActive, { dispatch }) => {
    try {
        const response = await handleIsActiveService(params);
        dispatch(
            openSnackbar({
                message: 'Service successfully configurated',
                variant: 'success'
            })
        );
        return response.data;
    } catch (error: any) {
        dispatch(
            openSnackbar({
                message: error?.response?.data?.message || 'An error occurred configuring AI',
                variant: 'error'
            })
        );
    }
});

export const editMerchant = createAsyncThunk(`${slice.name}/editMerchant`, async (data: EditMerchant, { dispatch }) => {
    try {
        const response = await editMerchantService(data);
        dispatch(
            openSnackbar({
                message: 'Merchant successfully edited',
                variant: 'success'
            })
        );
        return response.data;
    } catch (error: any) {
        dispatch(
            openSnackbar({
                message: error?.response?.data?.message || `An error occurred editing ${data.merchant}`,
                variant: 'error'
            })
        );
    }
});

export const createMerchant = createAsyncThunk(`${slice.name}/createMerchant`, async (data: CreateMerchant, { dispatch }) => {
    try {
        const response = await createMerchantService(data);
        dispatch(
            openSnackbar({
                message: 'Merchant successfully created',
                variant: 'success'
            })
        );
        return response.data;
    } catch (error: any) {
        dispatch(
            openSnackbar({
                message: error?.response?.data?.message || `An error occurred creating Merchant ${data.name}`,
                variant: 'error'
            })
        );
    }
});

export const createEditWebhook = createAsyncThunk(`${slice.name}/createEditWebhook`, async (webhook: Webhook[], { dispatch }) => {
    try {
        const response = await createEditWebhookService(webhook);
        dispatch(
            openSnackbar({
                message: 'Changes Saved',
                variant: 'success'
            })
        );
        return response.data;
    } catch (error: any) {
        dispatch(
            openSnackbar({
                message: error?.response?.data?.message || 'An error occurred while saving changes',
                variant: 'error'
            })
        );
    }
});

export const getWebhooks = createAsyncThunk(`${slice.name}/getWebhooks`, async (_arg, { dispatch }) => {
    try {
        const data = await getWebhooksService();
        return data;
    } catch (error) {
        dispatch(
            openSnackbar({
                message: 'An error occurred while fetching Webhooks.',
                variant: 'error'
            })
        );
        throw error;
    }
});

export const getBilling = createAsyncThunk(`${slice.name}/getBilling`, async (_arg, { dispatch }) => {
    try {
        const { data } = await getBillingService();
        return data.response.data[0].data;
    } catch (error) {
        dispatch(
            openSnackbar({
                message: 'An error occurred while fetching Bills.',
                variant: 'error'
            })
        );
    }
});

export const sendInvoice = createAsyncThunk(`${slice.name}/sendInvoice`, async (email: string, { dispatch }) => {
    try {
        const response = await sendInvoiceService(email);
        dispatch(
            openSnackbar({
                message: `Invoice sent to ${email}`,
                variant: 'success'
            })
        );
        return response.data;
    } catch (error) {
        dispatch(
            openSnackbar({
                message: `An error occurred Sending bill to ${email}.`,
                variant: 'error'
            })
        );
    }
});

export const getLoginMethods = createAsyncThunk(`${slice.name}/getLoginMethods`, async (_arg, { dispatch }) => {
    try {
        const { data } = await getLoginMethodsService();
        return data.response.data[0].data;
    } catch (error) {
        dispatch(
            openSnackbar({
                message: 'An error occurred while fetching Login Methods.',
                variant: 'error'
            })
        );
    }
});

export const editLoginMethods = createAsyncThunk(
    `${slice.name}/editLoginMethods`,
    async (method: SAMLType | GoogleType | FacebookType | OAUTHType | MicrosoftType, { dispatch }) => {
        try {
            const response = await editLoginMethodsService(method);
            return response.data;
        } catch (error) {
            dispatch(
                openSnackbar({
                    message: `An error occurred while editing ${Object.keys(method)}.`,
                    variant: 'error'
                })
            );
        }
    }
);

export const createLoginMethods = createAsyncThunk(
    `${slice.name}/createLoginMethods`,
    async (method: SAMLType | GoogleType | FacebookType | OAUTHType | MicrosoftType, { dispatch }) => {
        try {
            const response = await createLoginMethodsService(method);
            return response.data;
        } catch (error) {
            dispatch(
                openSnackbar({
                    message: `An error occurred while creating Login Method ${Object.keys(method)}.`,
                    variant: 'error'
                })
            );
        }
    }
);

export const getBalance = createAsyncThunk(`${slice.name}/getBalance`, async (_arg, { dispatch }) => {
    try {
        const response = await getBalanceService();
        return response.data.response;
    } catch (error) {
        dispatch(
            openSnackbar({
                message: 'An error occurred while fetching account balance.',
                variant: 'error'
            })
        );
    }
});

export const getTerms = createAsyncThunk(`${slice.name}/getTerms`, async (_arg, { dispatch }) => {
    try {
        const { data } = await getTermsService();
        return data[0].data.conditions;
    } catch (error) {
        dispatch(
            openSnackbar({
                message: 'An error occurred while fetching Terms and Conditions.',
                variant: 'error'
            })
        );
    }
});

export const patchConditions = createAsyncThunk(`${slice.name}/patchTerms`, async (conditions: string, { dispatch }) => {
    try {
        await patchConditionsService(conditions);
        dispatch(
            openSnackbar({
                message: 'Terms and Conditions successfully saved.',
                variant: 'success'
            })
        );
        return true;
    } catch (error) {
        dispatch(
            openSnackbar({
                message: 'An error occurred while saving Terms and Conditions.',
                variant: 'error'
            })
        );
    }
});
